import { Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
    getCommissions,
    getCommission,
    createCommission,
    updateCommission,
    deleteCommission,
} from '../../../api/commission.api';
import styles from '../../../assets/scss/pages/CommissionList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import KeypadInput from '../../../components/common/KeypadInput';

function formatValue(value, type) {
    const num = Number(value) || 0;
    if (type === 'percent') return `${num}%`;
    return `${num.toLocaleString('vi-VN')} VNĐ`;
}

export default function CommissionList() {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PERMISSIONS.COMMISSION.CREATE);
    const canUpdate = hasPermission(PERMISSIONS.COMMISSION.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.COMMISSION.DELETE);

    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toastMsg, setToastMsg] = useState(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ name: '', value: '', type: 'percent', description: '' });
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getCommissions();
            setCommissions(data.commissions || []);
        } catch {
            setCommissions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // Open create modal
    const openCreate = () => {
        setModalMode('create');
        setEditId(null);
        setForm({ name: '', value: '', type: 'percent', description: '' });
        setFormError('');
        setShowModal(true);
    };

    // Open edit modal
    const openEdit = async (id) => {
        setModalMode('edit');
        setEditId(id);
        setFormError('');
        try {
            const { data } = await getCommission(id);
            const c = data.commission || data;
            setForm({
                name: c.name || '',
                value: String(Math.round(Number(c.value) || 0)),
                type: c.type || 'percent',
                description: c.description || '',
            });
        } catch {
            const c = commissions.find((x) => x.id === id);
            if (c) setForm({ name: c.name, value: String(c.value), type: c.type, description: c.description || '' });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormError('');
    };

    // Save
    const handleSave = async () => {
        if (!form.name.trim() || !form.value) {
            setFormError('Vui lòng nhập đầy đủ tên và giá trị');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const payload = {
                name: form.name.trim(),
                value: form.value,
                type: form.type,
                description: form.description.trim(),
            };
            if (modalMode === 'create') {
                await createCommission(payload);
                showToast('success', 'Tạo hoa hồng thành công');
            } else {
                await updateCommission(editId, payload);
                showToast('success', 'Cập nhật thành công');
            }
            closeModal();
            fetchList();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Thao tác thất bại');
        } finally {
            setSaving(false);
        }
    };

    // Delete
    const handleDelete = async () => {
        if (!canDelete || !editId) return;
        if (!window.confirm('Bạn có chắc muốn xoá hoa hồng này?')) return;
        try {
            await deleteCommission(editId);
            showToast('success', 'Xoá thành công');
            closeModal();
            fetchList();
        } catch {
            showToast('error', 'Xoá thất bại', 2500);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Quản lý hoa hồng</h1>
                    <p className={styles.subtitle}>{commissions.length} loại hoa hồng</p>
                </div>
                {canCreate && (
                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openCreate}>
                        <Plus /> Tạo hoa hồng
                    </button>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner} />
                    Đang tải...
                </div>
            ) : commissions.length === 0 ? (
                <div className={styles.empty}>Chưa có loại hoa hồng nào</div>
            ) : (
                <div className={styles.grid}>
                    {commissions.map((c) => (
                        <div key={c.id} className={styles.card} onClick={() => openEdit(c.id)}>
                            <div className={styles.cardName}>{c.name}</div>
                            <div className={styles.cardValue}>{formatValue(c.value, c.type)}</div>
                            <div className={styles.cardType}>
                                {c.type === 'percent' ? 'Phần trăm đơn hàng' : 'Số tiền cố định'}
                            </div>
                            {c.description && <div className={styles.cardDesc}>{c.description}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {modalMode === 'create' ? 'Tạo hoa hồng' : 'Chi tiết hoa hồng'}
                            </h3>
                            <button className={styles.modalClose} onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className={styles.modalBody}>
                            {formError && <div className={styles.formError}>{formError}</div>}

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Tên hoa hồng <span>*</span></label>
                                <input
                                    className={styles.formInput}
                                    value={form.name}
                                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                    placeholder="VD: Hoa hồng cơ bản"
                                    readOnly={modalMode === 'edit' && !canUpdate}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Loại <span>*</span></label>
                                <div className={styles.typeToggle}>
                                    <button
                                        className={`${styles.typeBtn}${form.type === 'percent' ? ` ${styles.typeBtnActive}` : ''}`}
                                        onClick={() => setForm((p) => ({ ...p, type: 'percent' }))}
                                        disabled={modalMode === 'edit' && !canUpdate}
                                    >
                                        %
                                    </button>
                                    <button
                                        className={`${styles.typeBtn}${form.type === 'fixed' ? ` ${styles.typeBtnActive}` : ''}`}
                                        onClick={() => setForm((p) => ({ ...p, type: 'fixed' }))}
                                        disabled={modalMode === 'edit' && !canUpdate}
                                    >
                                        VNĐ
                                    </button>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Giá trị <span>*</span></label>
                                <KeypadInput
                                    className={styles.formInput}
                                    mode="integer"
                                    value={form.value}
                                    onChange={(raw) => setForm((p) => ({ ...p, value: raw }))}
                                    title="Giá trị hoa hồng"
                                    suffix={form.type === 'fixed' ? 'đ' : '%'}
                                    placeholder={form.type === 'percent' ? 'VD: 10' : 'VD: 100,000'}
                                    disabled={modalMode === 'edit' && !canUpdate}
                                />
                                <div className={styles.formHint}>
                                    Hoa hồng được tính dựa trên tổng số tiền của đơn hàng
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Ghi chú</label>
                                <textarea
                                    className={styles.formTextarea}
                                    value={form.description}
                                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                    placeholder="Mô tả (tuỳ chọn)"
                                    readOnly={modalMode === 'edit' && !canUpdate}
                                />
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            {modalMode === 'edit' && canDelete && (
                                <button className={`${styles.modalBtn} ${styles.modalBtnDanger}`} onClick={handleDelete}>
                                    Xoá
                                </button>
                            )}
                            <div style={{ flex: 1 }} />
                            <button className={styles.modalBtn} onClick={closeModal}>Đóng</button>
                            {(modalMode === 'create' || canUpdate) && (
                                <button className={`${styles.modalBtn} ${styles.modalBtnPrimary}`} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Đang lưu...' : modalMode === 'create' ? 'Tạo' : 'Lưu'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div className={styles.toastIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
